import React from 'react'
import styled from 'styled-components'
import CheckIcon from '@8x8/oxygen-icon/icons/CheckCircleSolidIcon'
import PropTypes from 'prop-types'

import VideoMeetingsLogo from '~/components/icons/meet-logo'
import { Flex, ColumnFlex, ColumnFlexCentered } from '~/components/common/containers'
import Delimiter from '~/components/common/delimiter'
import { translate } from '~/core/translate'
import OrderSummary from '../order-summary'
import Features from '../features'
import Button from '~/components/common/button'
import { PRODUCT_ADVERTISED_FEATURES } from '~/core/constants'

const Name = styled.b`
  margin-top: 8px;
  margin-bottom: 16px;
  font-size: 20px;
  font-weight: 600;
  text-align: left;
  color: #1c2025;
`

const Price = styled.span`
  color: #000;
  font-size: 16px;
`

const UpgradeButton = styled(Button)`
  width: 100%;
  max-width: 290px;
  height: 40px;
  margin-top: 24px;
  margin-bottom: 16px;
`

const Content = styled(ColumnFlexCentered)`
  max-width: 290px;
  padding: 16px 16px 26px 16px;
`

const ProductCard = styled(ColumnFlexCentered)`
  box-shadow: 0px 8px 16px #D1DBE8;
  margin-bottom: 24px;
  box-sizing: border-box;

  a {
    color: #0376da !important;
  }
`

const TermsAndConditions = styled.span`
  font-size: 13px;
  line-height: 20px;
  color: #5e6d7a;
  padding: 16px 16px 24px 16px;
`

const StyledCheckIcon = styled(CheckIcon).attrs({
  size: 20
})`
  fill: #0376da;
  margin-right: 8px;
`

const Checkline = styled(Flex)`
  margin-bottom: 16px;
  font-size: 13px;
  font-weight: 600;
`

const Container = styled(ColumnFlexCentered)`
  @media only screen and (min-width: 768px) and (min-height: 376px) {
    max-width: 500px;
  }
`

const Product = ({ product, onGetSession }) => {
  const { name, plan } = product
  const { price } = plan
  const [value, decimals] = price.split('.')

  return (
    <Container fullWidth>
      <ProductCard fullWidth>
        <Content>
          <VideoMeetingsLogo size={32} />
          <Name>{name}</Name>
          <Price>
            <b>
              {value}
              {Boolean(decimals) && <>.{decimals}</>}
            </b>
            &nbsp;
            {translate(plan.recurrence)}
          </Price>
          <UpgradeButton
            onClick={() => onGetSession(plan.id)}
            data-test-id={`${name} button`}
          >
            {translate('continue')}
          </UpgradeButton>

          <ColumnFlex>
            <Checkline>
              <StyledCheckIcon />
              {translate('no_charge_until_trial_ends')}
            </Checkline>

            <Checkline>
              <StyledCheckIcon />
              {translate('no_contracts')}
            </Checkline>
          </ColumnFlex>
        </Content>

        <Delimiter isFullLength />

        <TermsAndConditions>{translate('terms_and_conditions_apply')}
          <a
            href={`${window.location.origin}/terms-of-service`}
            target='_blank'
            rel='noopener noreferrer'

          >
            {translate('terms_and_conditions_apply_link_text')}
          </a>
        </TermsAndConditions>
      </ProductCard>
      <Features features={plan.advertisedFeatures} />
      <OrderSummary plan={plan} />
    </Container>
  )
}

Product.propTypes = {
  product: PropTypes.shape({
    name: PropTypes.string,
    plan: PropTypes.shape({
      advertisedFeatures: PropTypes.arrayOf(PropTypes.oneOf(Object.keys(PRODUCT_ADVERTISED_FEATURES))),
      id: PropTypes.string,
      price: PropTypes.string,
      recurrence: PropTypes.string,
      billingRecurrence: PropTypes.string
    })
  }),
  onGetSession: PropTypes.func
}

export default Product
